/**
 * Central cache management utility for Company Analysis app
 * Handles caching and retrieval of data with consistent expiration policies
 */

// Cache configuration
const DEFAULT_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const APP_CACHE_PREFIX = 'company_analysis_';
const NON_CRITICAL_UI_PREFIXES = [
  'watchlists.state.v1',
  'watchlists.compare.state.v1',
  'comparison.recent.tickers.v1',
  'cachedTickers',
  'cachedTickers.v',
];

const getParsedCacheEntry = (key) => {
  try {
    const rawValue = localStorage.getItem(key);
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const pruneExpiredAppCache = () => {
  Object.keys(localStorage).forEach((key) => {
    if (!key.startsWith(APP_CACHE_PREFIX)) return;
    const parsed = getParsedCacheEntry(key);
    const expiry = Number(parsed?.expiry);
    if (Number.isFinite(expiry) && Date.now() > expiry) {
      localStorage.removeItem(key);
    }
  });
};

export const compactAppCache = (targetRemovals = 8) => {
  const cacheEntries = Object.keys(localStorage)
    .filter((key) => key.startsWith(APP_CACHE_PREFIX))
    .map((key) => {
      const parsed = getParsedCacheEntry(key);
      return {
        key,
        expiry: Number(parsed?.expiry) || Number.MAX_SAFE_INTEGER,
        timestamp: Number(parsed?.timestamp) || 0,
      };
    })
    .sort((left, right) => {
      if (left.expiry !== right.expiry) return left.expiry - right.expiry;
      return left.timestamp - right.timestamp;
    });

  cacheEntries.slice(0, Math.max(0, targetRemovals)).forEach(({ key }) => {
    localStorage.removeItem(key);
  });
};

export const compactNonCriticalUiState = (targetRemovals = 10) => {
  const removableKeys = Object.keys(localStorage)
    .filter((key) => NON_CRITICAL_UI_PREFIXES.some((prefix) => key.startsWith(prefix)))
    .sort((left, right) => {
      const leftLength = String(localStorage.getItem(left) || '').length;
      const rightLength = String(localStorage.getItem(right) || '').length;
      return rightLength - leftLength;
    });

  removableKeys.slice(0, Math.max(0, targetRemovals)).forEach((key) => {
    localStorage.removeItem(key);
  });
};

export const setLocalStorageWithCleanup = (key, value, options = {}) => {
  const targetRemovals = Number(options.targetRemovals) > 0 ? Number(options.targetRemovals) : 10;

  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    const isQuotaError = error?.name === 'QuotaExceededError'
      || String(error?.message || '').toLowerCase().includes('quota');

    if (!isQuotaError) {
      console.error('Error setting localStorage value:', error);
      return false;
    }

    try {
      pruneExpiredAppCache();
      compactAppCache(Math.max(6, Math.floor(targetRemovals / 2)));
      compactNonCriticalUiState(targetRemovals);
      localStorage.setItem(key, value);
      return true;
    } catch (retryError) {
      console.error('Error setting localStorage value after cleanup:', retryError);
      return false;
    }
  }
};

/**
 * Set data in cache with expiration
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} duration - Cache duration in milliseconds (defaults to 24 hours)
 */
export const setCachedData = (key, data, duration = DEFAULT_CACHE_DURATION) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + duration
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
    return true;
  } catch (error) {
    if (error?.name === 'QuotaExceededError' || String(error?.message || '').toLowerCase().includes('quota')) {
      try {
        pruneExpiredAppCache();
        compactAppCache();
        const cacheEntry = {
          data,
          timestamp: Date.now(),
          expiry: Date.now() + duration
        };
        localStorage.setItem(key, JSON.stringify(cacheEntry));
        return true;
      } catch (retryError) {
        console.error('Error setting cache after compaction:', retryError);
        return false;
      }
    }

    console.error('Error setting cache:', error);
    return false;
  }
};

/**
 * Get data from cache if still valid
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null if expired/not found
 */
export const getCachedData = (key) => {
  try {
    const cachedEntry = localStorage.getItem(key);
    if (!cachedEntry) return null;
    
    const { data, expiry } = JSON.parse(cachedEntry);
    
    // Check if cache has expired
    if (Date.now() > expiry) {
      localStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error retrieving from cache:', error);
    return null;
  }
};

/**
 * Clear specific cache entry
 * @param {string} key - Cache key to clear
 */
export const clearCacheItem = (key) => {
  localStorage.removeItem(key);
};

/**
 * Clear all app cache data
 */
export const clearAllCache = () => {
  // Find and clear only keys related to our app (to avoid clearing other app data)
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(APP_CACHE_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Generate a cache key for financial data
 * @param {string} type - Data type (e.g., 'income', 'cashflow', 'metrics')
 * @param {string} ticker - Stock ticker
 * @param {string} period - Optional period
 * @returns {string} - Formatted cache key
 */
export const generateCacheKey = (type, ticker, period = null) => {
  const base = `company_analysis_${type}_${ticker}`;
  return period ? `${base}_${period}` : base;
};
